import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FormEvent, useId, useMemo, useState } from 'react';
import {
  useAuthUserRestaurant,
  useEditProductOfferOrderMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';
import { ProductOfferOrder } from '@durma-soft/gros-sdk/dist/types/product-offers';

import { Modal } from '@/components/shared/modal';
import { Input } from '@/components/shared/shadcn-ui/input';
import { Label } from '@/components/shared/shadcn-ui/label';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';

import { formatPrice } from '@/utils/product-offers';

interface EditProductOfferOrderModalProps {
  order: ProductOfferOrder;
  onClose: () => unknown;
}

export const EditProductOfferOrderModal = ({
  order,
  onClose,
}: EditProductOfferOrderModalProps) => {
  const { t } = useTranslation();
  const uid = useId();
  const restaurant = useAuthUserRestaurant();

  const [quantity, setQuantity] = useState<number | null>(
    Number(order.quantity),
  );

  const editOrder = useEditProductOfferOrderMutation();

  const isValid = useMemo(() => quantity != null && quantity > 0, [quantity]);

  const total = useMemo(() => {
    if (!isValid) return null;
    return (Number(order.price_per_unit) * quantity!).toFixed(2);
  }, [isValid, quantity, order.price_per_unit]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      await editOrder.mutateAsync({
        restaurant_id: restaurant.id,
        product_offer_order_id: order.id,
        quantity: quantity!,
      });
      onClose();
      toast.success(t('product-offers.edit-order-success-msg'));
    } catch (error) {
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('product-offers.edit-order-error-msg'));
    }
  };

  return (
    <Modal
      isModalOpen
      closeModal={onClose}
      title={t('product-offers.edit-order')}
      description={t('product-offers.edit-order-description', {
        productOffer: order.product_offer.name,
      })}
    >
      <form onSubmit={handleSubmit}>
        <div className="grid gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor={uid + '-quantity'}>
              {t('common.quantity')} ({order.unit})
            </Label>
            <Input
              id={uid + '-quantity'}
              min={0.01}
              step="any"
              type="number"
              inputMode="decimal"
              value={quantity ?? ''}
              onChange={(e) =>
                setQuantity(e.target.value === '' ? null : +e.target.value)
              }
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {t('product-offers.price-per-unit')}:{' '}
            {formatPrice(order.price_per_unit)} {order.currency}
            {total && (
              <>
                {' — '}
                {t('product-offers.total')}: {total} {order.currency}
              </>
            )}
          </p>
        </div>
        <DialogFooter className="mt-2">
          <Button
            type="submit"
            disabled={!isValid}
            isLoading={editOrder.isPending}
          >
            {t('common.save-edit')}
          </Button>
        </DialogFooter>
      </form>
    </Modal>
  );
};
